import React, {
	type ComponentType,
	createContext,
	type PropsWithChildren,
	type ReactNode,
	useContext,
} from 'react';

import { type ExtensionParams } from '@atlaskit/editor-common/extensions';
import { type MediaProvider } from '@atlaskit/editor-common/provider-factory';
import type { LinkPickerPlugin } from '@atlaskit/link-picker';
import type { Auth, AuthProvider } from '@atlaskit/media-core/auth';
import type { UploadRejectionData } from '@atlaskit/media-picker/types';
import type { BuiltInColorMap } from '@atlassian/color-picker';

import { type ExtensionMode, type FeedbackType } from '../../types';
import { type HubDefaultParameters } from '../get-hub-default-parameters';

export interface ExperienceTracker<BaseStartParams = { analyticsSource: string }> {
	abort?: (reason: { reason: string }) => void;
	start: (params: BaseStartParams) => void;
	stopOnError: ({ error }: { error: Error }) => void;
	succeed: () => void;
}

type AiDescriptionStartParams = { analyticsSource: string; confluencePageId: string };
export type AiDescriptionExperienceTracker = ExperienceTracker<AiDescriptionStartParams>;

export type CustomMediaConfig = {
	asapIssuer: string;
	mediaCollectionName: string;
	mediaToken: string;
	mediaUrl: string;
};

export interface ProductDependenciesProviderProps {
	children?: ReactNode;
	cloudId?: string;
	contentId: string;
	customMediaConfig?: CustomMediaConfig;
	// Optional override components
	errorBoundaryComponent?: ComponentType<PropsWithChildren<NonNullable<unknown>>>;
	experienceStartComponent?: ComponentType<NonNullable<unknown>>;
	extensionNode?: ExtensionParams<Record<string, any>>;
	helpCenterAri?: string;
	isCompanyHub?: boolean;
	mode: ExtensionMode;
}

export type UseMediaConfig = {
	authProvider: AuthProvider;
	collection: string;
	fileId: string;
};

export type Branding = {
	brandBackgroundColorMap: BuiltInColorMap;
	colors?: {
		primary?: string;
	};
	defaultParameters?: HubDefaultParameters;
	hideDefaultColorPalette?: boolean;
	logo?: {
		fileId?: string;
		url?: string;
	};
};
export interface ProductDependenciesProviderResponse {
	aiDescriptionExperienceTracker?: AiDescriptionExperienceTracker;
	// Analytics & Feedback
	analyticsContext?: Record<string, any>;

	branding?: Branding;

	cloudId: string;
	cloudUrl?: string;

	// Premium upsells
	ConfigUpsellScreen?: ComponentType<{ feature: string }>;
	// Media Providers
	createEditorMediaProvider?: (options: {
		contentId: string;
		contentType: string;
		isExternalShare: boolean;
		onUploadRejection?: ((data: UploadRejectionData) => boolean) | undefined;
	}) => Promise<MediaProvider>;

	customMediaConfig?: CustomMediaConfig;

	displayName?: string;

	enableLinkInputInMediaPicker?: boolean;

	enableSmartCardsFields?: boolean;

	enableTargeting?: boolean;

	enableUnifiedSearchFeatures?: boolean;

	errorBoundaryComponent?: ComponentType<PropsWithChildren<NonNullable<unknown>>>;

	// ~ OPTIONAL DEPENDENCIES ~
	errorDisplayComponent?: ComponentType<PropsWithChildren<{ error: Error }>>;
	// Experience Tracking
	experienceStartComponent?: ComponentType<NonNullable<unknown>>;
	experienceStopComponent?: ComponentType<{ error?: Error; shouldForceError?: boolean }>;

	experienceSuccessComponent?: ComponentType<NonNullable<unknown>>;
	helpCenterAri?: string;
	// custom requirements to hide border for certain elements
	// in unified hubs: this should be replaced by border config
	// on per-element basis
	hideExtensionBorders?: boolean;

	hoistedProjectKey?: string;
	// AI and Company Hub
	isAdminHubAIEnabled?: boolean;
	isCompanyHub: boolean;
	/**
	 * True only when the shared hub extensions are rendered inside a Help Hub
	 * (help-center `hubType === 'HELP_HUB'`). Undefined/false on every other
	 * surface (CSM help centers, Confluence company hub). Used to scope
	 * Help-Hub-only extension affordances without reading `HubContext` from
	 * shared code (which throws outside a hub).
	 */
	isHelpHub?: boolean;

	// Theming
	isSSRProductInDarkMode?: boolean;
	isTargetAudience: (targetGroupIds: string[] | undefined) => boolean;

	// Link picker configuration
	linkPickerConfig?: {
		linkPickerPlugins?: LinkPickerPlugin[];
	};

	markErrorAsHandled?: (error: Error) => void;
	mediaUploadExperienceTracker?: ExperienceTracker;
	orgId?: string;
	// Custom search configuration
	searchConfigProvider?: {
		advancedSearchUrl?: string;
		peopleDirectoryUrl?: string;
		searchSessionId?: string;
	};
	// Shortcut listener
	shortcutListener?: ComponentType<{ accelerator: string; listener: () => void }>;
	showConfigPreambleSupportLink?: boolean;

	smartCardProviderComponent: ComponentType<PropsWithChildren<NonNullable<unknown>>> | undefined;
	// Error Handling
	submitError?: (error: Error) => void;
	transformUploadedImageId?: (options: { contentId: string; fileId: string }) => string;

	useAnalyticsActions?: { [key: string]: any };

	useFeedbackCollector?: (options: { source: string; type: FeedbackType }) => () => void;
	useMediaConfig?: (options: { contentId: string; imageSrc: string }) => UseMediaConfig | null;
	userId: string;

	// User segmentation
	useSegmentationTargetGroups: ({
		nextCursor,
		initialGroupIds,
	}: {
		initialGroupIds?: string[];
		nextCursor?: string;
	}) => any;
}

export const ProductDependenciesContext: React.Context<ProductDependenciesProviderResponse> =
	createContext<ProductDependenciesProviderResponse>({
		// AI and Company Hub
		isCompanyHub: true,
		isHelpHub: false,
		isAdminHubAIEnabled: false,
		enableLinkInputInMediaPicker: false,
		helpCenterAri: '',

		showConfigPreambleSupportLink: false,
		// Shortcuts
		shortcutListener: undefined,

		// Smart cards
		smartCardProviderComponent: undefined,

		// Custom search configuration
		orgId: '',
		userId: '',
		searchConfigProvider: {
			peopleDirectoryUrl: '',
			advancedSearchUrl: '',
			searchSessionId: '',
		},
		displayName: '',

		// Theming
		isSSRProductInDarkMode: undefined,
		branding: undefined,
		hideExtensionBorders: false,

		// Feedback collection
		useFeedbackCollector: undefined,

		// Media providers
		cloudId: '',
		useMediaConfig: undefined,
		createEditorMediaProvider: () =>
			Promise.resolve({
				viewMediaClientConfig: {
					authProvider: async () => ({}) as Auth,
				},
				uploadMediaClientConfig: {
					authProvider: async () => ({}) as Auth,
				},
			} as MediaProvider),

		// Error handling
		submitError: () => {},
		markErrorAsHandled: () => {},
		errorDisplayComponent: () => null,
		errorBoundaryComponent: undefined,

		// Premium upsells
		ConfigUpsellScreen: undefined,

		// Experience tracking
		mediaUploadExperienceTracker: undefined,
		aiDescriptionExperienceTracker: undefined,
		experienceSuccessComponent: () => null,
		experienceStopComponent: () => null,
		experienceStartComponent: () => null,

		// Analytics
		analyticsContext: {},

		// User segmentation
		useSegmentationTargetGroups: () => {},
		isTargetAudience: () => false,

		// Link picker configuration
		linkPickerConfig: {},

		enableTargeting: false,
		enableUnifiedSearchFeatures: false,
		enableSmartCardsFields: false,
	});

export const ProductDependenciesProvider = ({
	children,
	...props
}: PropsWithChildren<ProductDependenciesProviderProps>): React.JSX.Element => {
	const defaultValue = useContext(ProductDependenciesContext);
	const value = { ...defaultValue, ...props };

	return (
		<ProductDependenciesContext.Provider value={value}>
			{children}
		</ProductDependenciesContext.Provider>
	);
};

export function useProductDependencies(): ProductDependenciesProviderResponse {
	const ctx = useContext<ProductDependenciesProviderResponse>(ProductDependenciesContext);
	if (!ctx) {
		throw new Error('useProductDependencies must be used within ProductDependenciesProvider');
	}
	return ctx;
}
